import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  IconButton,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  HomeOutlined as HomeIconOutlined,
  BusinessOutlined as BusinessIconOutlined,
  CalendarMonthOutlined as CalendarIconOutlined,
  ChatBubbleOutlineOutlined as ChatBubbleOutlineOutlinedIcon,
  SmartToyOutlined as SmartToyOutlinedIcon,
  DirectionsCarOutlined as CarIconOutlined,
  BuildOutlined as BuildIconOutlined,
  PeopleOutlined as PeopleIconOutlined,
  BarChartOutlined as AnalyticsIconOutlined,
  ManageAccountsOutlined as ManageAccountsIconOutlined,
  AdminPanelSettingsOutlined as AdminIconOutlined,
  PushPinOutlined as PushPinIconOutlined,
  PushPin as PushPinIcon,
  RoomPreferencesOutlined as RoomPreferencesIconOutlined,
} from '@mui/icons-material';
import logoGM from '../../assets/logo_gm_white.png';

const DRAWER_WIDTH = 240;
const DRAWER_WIDTH_CLOSED = 72;

const Sidebar = ({ open, onClose, isPinned, onTogglePin, isMobile }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = [
    { text: t('menu.home'), icon: <HomeIconOutlined />, path: '/dashboard', disabled: false },
    { text: t('menu.appointments'), icon: <CalendarIconOutlined />, path: '/appointments', disabled: true },
    { text: t('menu.aiAdjustments'), icon: <SmartToyOutlinedIcon />, path: '/ai-adjustments', disabled: true },
    { text: t('menu.analytics'), icon: <AnalyticsIconOutlined />, path: '/analytics', disabled: true },
    { text: t('menu.service'), icon: <ChatBubbleOutlineOutlinedIcon />, path: '/service', disabled: true },
    { text: t('menu.serviceCatalog'), icon: <BuildIconOutlined />, path: '/services', disabled: true },
    { text: t('menu.vehicleCatalog'), icon: <CarIconOutlined />, path: '/vehicles', disabled: true },
    { text: t('menu.customers'), icon: <PeopleIconOutlined />, path: '/customers', disabled: true },
    { text: t('menu.dealershipManagement'), icon: <BusinessIconOutlined />, path: '/dealership', disabled: true },
    { text: t('menu.businessManagement'), icon: <RoomPreferencesIconOutlined />, path: '/business-management', disabled: false },
    { text: t('menu.userManagement'), icon: <ManageAccountsIconOutlined />, path: '/users', disabled: false },
    { text: t('menu.settings'), icon: <AdminIconOutlined />, path: '/settings', disabled: true },
  ];

  const handleNavigate = (path, disabled) => {
    if (disabled) return;
    navigate(path);
    if (isMobile) {
      onClose();
    }
  };


  const drawerContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#194390',
        color: 'white',
      }}
    >
      {/* Logo Section */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: open ? 'space-between' : 'center',
          height: 64,
          minHeight: 64,
        }}
      >
        <Box
          component="img"
          src={logoGM}
          alt="GM Logo"
          sx={{
            height: 40,
            objectFit: 'contain',
            transition: 'opacity 0.3s',
            opacity: open ? 1 : 0.9,
          }}
        />
        {open && !isMobile && (
          <IconButton
            onClick={onTogglePin}
            size="small"
            sx={{ color: 'white' }}
          >
            {isPinned ? <PushPinIcon fontSize="small" /> : <PushPinIconOutlined fontSize="small" />}
          </IconButton>
        )}
      </Box>

      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.12)' }} />

      {/* Menu Items */}
      <List sx={{ flexGrow: 1, py: 2, overflow: 'auto' }}>
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path;

          return (
            <Tooltip
              key={item.text}
              title={!open ? item.text : ''}
              placement="right"
              arrow
            >
              <ListItem
                disablePadding
                sx={{
                  px: 1,
                  mb: 0.5,
                }}
              >
                <ListItemButton
                  onClick={() => handleNavigate(item.path, item.disabled)}
                  disabled={item.disabled}
                  sx={{
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    px: 2,
                    py: 1.2,
                    minHeight: 44,
                    backgroundColor: isActive ? '#E3F2FD' : 'transparent',
                    opacity: item.disabled ? 0.5 : 1,
                    cursor: item.disabled ? 'not-allowed' : 'pointer',
                    '&:hover': {
                      backgroundColor: item.disabled
                        ? 'transparent'
                        : isActive
                        ? '#BBDEFB'
                        : 'rgba(255, 255, 255, 0.08)',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      width: 24,
                      height: 24,
                      mr: open ? 2 : 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      color: isActive ? '#0372CF' : 'white',
                      '& svg': {
                        fontSize: '1.25rem',
                      },
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {open && (
                    <ListItemText
                      primary={item.text}
                      sx={{
                        color: isActive ? '#0372CF' : 'white',
                        '& .MuiTypography-root': {
                          fontSize: '0.8125rem',
                          fontWeight: isActive ? 600 : 400,
                          whiteSpace: 'nowrap',
                        },
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            </Tooltip>
          );
        })}
      </List>

      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.12)' }} />
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      open={open}
      sx={{
        width: open ? DRAWER_WIDTH : DRAWER_WIDTH_CLOSED,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: open ? DRAWER_WIDTH : DRAWER_WIDTH_CLOSED,
          boxSizing: 'border-box',
          overflowX: 'hidden',
          height: '100vh',
          borderRight: 'none',
          transition: (theme) =>
            theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;
